export type VehicleDocumentDirection = 'exit' | 'entry';

export interface VehicleDocumentLineLike {
  articleId: string;
  variantId?: string | null;
  variantLabel?: string | null;
  quantity: number;
}

export interface VehicleDocumentLike {
  id: string;
  documentType: VehicleDocumentDirection;
  parentDocumentId?: string | null;
  exitDate: string;
  /** Optional registration is represented by an empty string. */
  carRegistrationNumber: string;
  lines?: readonly VehicleDocumentLineLike[];
}

export interface VehicleArticleBalance {
  articleId: string;
  variantId?: string | null;
  variantLabel?: string | null;
  exitedQuantity: number;
  returnedQuantity: number;
  remainingQuantity: number;
}

export interface VehicleDocumentPeriodSummary {
  documentCount: number;
  totalQuantity: number;
  vehicleCount: number;
}

/** Stable identity for quantities; variants of one article never share a balance. */
export function vehicleLineKey(line: Pick<VehicleDocumentLineLike, 'articleId' | 'variantId'>): string {
  return line.variantId ? `${line.articleId}::${line.variantId}` : line.articleId;
}

const roundQuantity = (value: number): number =>
  Math.round((value + Number.EPSILON) * 1_000) / 1_000;

/**
 * Computes the still-outside quantity for one vehicle exit. Multiple source
 * or return lines for the same article and variant are folded together.
 */
export function vehicleExitBalances(
  source: VehicleDocumentLike,
  documents: readonly VehicleDocumentLike[],
): VehicleArticleBalance[] {
  if (source.documentType !== 'exit') return [];

  const exitedByArticle = new Map<string, number>();
  for (const line of source.lines ?? []) {
    if (
      !line.articleId ||
      !Number.isFinite(line.quantity) ||
      line.quantity <= 0
    )
      continue;
    exitedByArticle.set(
      vehicleLineKey(line),
      roundQuantity((exitedByArticle.get(vehicleLineKey(line)) ?? 0) + line.quantity),
    );
  }

  const returnedByArticle = new Map<string, number>();
  for (const document of documents) {
    if (
      document.documentType !== 'entry' ||
      document.parentDocumentId !== source.id
    ) {
      continue;
    }
    for (const line of document.lines ?? []) {
      if (
        !line.articleId ||
        !Number.isFinite(line.quantity) ||
        line.quantity <= 0
      ) {
        continue;
      }
      returnedByArticle.set(
        vehicleLineKey(line),
        roundQuantity(
          (returnedByArticle.get(vehicleLineKey(line)) ?? 0) + line.quantity,
        ),
      );
    }
  }

  return [...exitedByArticle.entries()].map(([key, exitedQuantity]) => {
    const sourceLine = source.lines!.find((line) => vehicleLineKey(line) === key)!;
    const returnedQuantity = returnedByArticle.get(key) ?? 0;
    return {
      articleId: sourceLine.articleId,
      ...(sourceLine.variantId ? { variantId: sourceLine.variantId, variantLabel: sourceLine.variantLabel } : {}),
      exitedQuantity,
      returnedQuantity,
      remainingQuantity: roundQuantity(
        Math.max(0, exitedQuantity - returnedQuantity),
      ),
    };
  });
}

/** Fail-closed guard used immediately before creating a vehicle entry. */
export function vehicleEntryFitsSource(
  source: VehicleDocumentLike,
  documents: readonly VehicleDocumentLike[],
  requestedLines: readonly VehicleDocumentLineLike[],
): boolean {
  const remaining = new Map(
    vehicleExitBalances(source, documents).map((line) => [
      vehicleLineKey(line),
      line.remainingQuantity,
    ]),
  );
  const requested = new Map<string, number>();

  for (const line of requestedLines) {
    if (
      !line.articleId ||
      !Number.isFinite(line.quantity) ||
      line.quantity <= 0
    )
      return false;
    requested.set(
      vehicleLineKey(line),
      roundQuantity((requested.get(vehicleLineKey(line)) ?? 0) + line.quantity),
    );
  }

  if (requested.size === 0) return false;
  return [...requested.entries()].every(
    ([articleId, quantity]) =>
      quantity <= (remaining.get(articleId) ?? 0) + Number.EPSILON,
  );
}

export function summarizeVehicleDocuments(
  documents: readonly VehicleDocumentLike[],
  from: string,
  to: string,
): VehicleDocumentPeriodSummary {
  const inPeriod = documents.filter(
    (document) => document.exitDate >= from && document.exitDate <= to,
  );
  return {
    documentCount: inPeriod.length,
    totalQuantity: roundQuantity(
      inPeriod.reduce(
        (sum, document) =>
          sum +
          (document.lines ?? []).reduce(
            (lineSum, line) =>
              lineSum +
              (Number.isFinite(line.quantity) && line.quantity > 0
                ? line.quantity
                : 0),
            0,
          ),
        0,
      ),
    ),
    vehicleCount: new Set(
      inPeriod
        .map((document) => document.carRegistrationNumber.trim().toUpperCase())
        .filter(Boolean),
    ).size,
  };
}
